/**
 * ServiceSelectorFormat 工具函数
 */
import type { IFieldMappingConfig, ICustomFilterItem } from './types';
import { ALL_VALUE } from './constants';

/**
 * 创建自定义字段映射配置的工具函数
 */
export const createFieldMapping = (
  config: Partial<IFieldMappingConfig> & Pick<IFieldMappingConfig, 'filterPath' | 'selectorPath'>
): IFieldMappingConfig => ({
  triggerValue: ALL_VALUE,
  ...config
});

/**
 * 默认的数据提取器
 * @description 从 selector 数据中提取 code 列表
 */
export const defaultExtractor = (selectorData: any[], codeField = 'code'): string[] => {
  if (!Array.isArray(selectorData)) return [];
  return selectorData.map((item) => {
    if (typeof item === 'string') return item;
    return item[codeField] || item.code || item.id || '';
  }).filter(Boolean);
};

/**
 * 根据级联配置提取数据
 */
export const extractByCascadeConfig = (
  selectorData: any[],
  cascadeConfig: IFieldMappingConfig['cascadeConfig']
): string[] => {
  if (!Array.isArray(selectorData) || !cascadeConfig) return [];

  const { typeField, typeValue, codeField } = cascadeConfig;

  return selectorData
    .filter((item) => item[typeField] === typeValue)
    .map((item) => item[codeField] || item.code || '')
    .filter(Boolean);
};

/**
 * 根据级联配置过滤用户已选的值，只保留属于指定类型的值
 * @description 当用户选择了具体值（非 255）时，过滤掉不属于指定类型的数据
 */
export const filterValuesByType = (
  currentValue: string[],
  selectorData: any[],
  cascadeConfig: IFieldMappingConfig['cascadeConfig']
): string[] => {
  if (!Array.isArray(selectorData) || !cascadeConfig || !Array.isArray(currentValue)) {
    return currentValue;
  }

  const { typeField, typeValue, codeField } = cascadeConfig;

  // 获取属于指定类型的所有有效 code
  const validCodes = new Set(
    selectorData
      .filter((item) => item[typeField] === typeValue)
      .map((item) => item[codeField] || item.code || '')
      .filter(Boolean)
  );

  // 过滤用户选择的值，只保留属于指定类型的值
  return currentValue.filter((val) => validCodes.has(val));
};

/**
 * 获取嵌套对象的值
 * @param obj - 目标对象
 * @param path - 路径字符串，如 'a.b.c'
 */
export const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

/**
 * 设置嵌套对象的值（返回新对象，不修改原对象）
 * @param obj - 目标对象
 * @param path - 路径字符串
 * @param value - 要设置的值
 */
export const setNestedValue = (obj: any, path: string, value: any): any => {
  const keys = path.split('.');
  const result = { ...obj };

  let current = result;
  for (let i = 0;i < keys.length - 1;i++) {
    const key = keys[i];
    current[key] = { ...current[key] };
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * 检查值是否匹配触发条件
 */
export const checkTriggerValue = (
  value: any,
  triggerValue: string | string[] = ALL_VALUE
): boolean => {
  if (!value) return false;

  const triggers = Array.isArray(triggerValue) ? triggerValue : [triggerValue];

  // 如果是数组，检查是否只包含触发值
  if (Array.isArray(value)) {
    return value.length === 1 && triggers.includes(value[0]);
  }

  return triggers.includes(value);
};

/**
 * 处理 custom_filters 中的字段
 */
export const processCustomFilters = (
  customFilters: ICustomFilterItem[],
  filterKey: string,
  newValue: string[]
): ICustomFilterItem[] => {
  if (!Array.isArray(customFilters)) return customFilters;

  return customFilters.map((item) => {
    if (item.key === filterKey) {
      return { ...item, filter_list: newValue };
    }
    return item;
  });
};

/**
 * 获取 custom_filters 中指定 key 的 filter_list
 */
export const getCustomFilterValue = (
  customFilters: ICustomFilterItem[],
  filterKey: string
): string[] | undefined => {
  if (!Array.isArray(customFilters)) return undefined;
  const item = customFilters.find((f) => f.key === filterKey);
  return item?.filter_list;
};
